import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import DateRangePicker from '../../components/ui/Calendar/DateRangePicker';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  TextField,
} from '@mui/material';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import {
  listTourBookings,
  setTourBookingStatus,
  cancelTourBooking,
  getToursByIds,
  money
} from '../../api/tourApi';
import socket from '../../socket';
import useTourNotificationStore from '../../stores/tourNotificationStore';
import { fetchServiceCategories } from '../../api/hotelinfoApi';

// --- format helpers (clean, consistent) ---
const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
};
const fmtDateTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
};
const fmtTime = (v) => {
  if (!v) return '-';
  const s = String(v).trim();
  // If already contains AM/PM, return as-is (normalized spacing)
  if (/\b(am|pm)\b/i.test(s)) return s.replace(/\s+/g, ' ').toUpperCase();
  // Handle HH:mm or H:mm
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    let hour = parseInt(m[1], 10);
    const minute = m[2];
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minute} ${ampm}`;
  }
  // Fallback: try Date parsing
  const d = new Date(`1970-01-01T${s}`);
  if (!isNaN(d)) {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
  }
  return s; // as-is if unrecognized format
};
const shortId = (id) => (id ? id.slice(-6) : '-');

// Normalize to UTC YYYY-MM-DD
const ymd = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Normalize API status strings to consistent UI buckets
const statusCategory = (status) => {
  const raw = (status || '').toString().toLowerCase().trim();
  if (!raw) return 'unknown';
  if (raw.includes('await') || raw.includes('wait') || raw.includes('pending')) return 'waiting';
  if (raw.includes('cancel')) return 'cancelled';
  if (raw.includes('complete')) return 'completed';
  if (raw.includes('confirm')) return 'confirmed';
  return raw;
};

/**
 * TourBookings — Staff-side management UI for tour bookings
 * Props:
 *  - propertyId (string): Active property to filter bookings
 */
export default function TourBookings({ propertyId }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | waiting | confirmed | completed | cancelled
  const STORAGE_KEY = 'tourBookings.selectedCategory';
  const [selectedCategory, setSelectedCategory] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved || 'all';
    } catch {
      return 'all';
    }
  });
  const [query, setQuery] = useState('');
  const [servicesById, setServicesById] = useState({}); // { [tourId]: { title, imageUrls, ... } }
  const [categories, setCategories] = useState([]); // service.category list from HotelService
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState('');     // YYYY-MM-DD
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const createDefaultRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return [{ startDate: start, endDate: end, key: 'selection' }];
  };
  const [dateRange, setDateRange] = useState([]);
  const pickerWrapRef = useRef(null);
  const [datePickerRect, setDatePickerRect] = useState(null);

  // Normalize bookings: if tourId is a populated object, extract it to servicesById and replace with string ID
  const handleNewBookingsData = useCallback((newBookings) => {
    const extractedServices = {};
    const normalizedBookings = (newBookings || []).map(b => {
      if (b && typeof b.tourId === 'object' && b.tourId !== null && b.tourId._id) {
        const tour = b.tourId;
        extractedServices[tour._id] = tour;
        return { ...b, tourId: tour._id };
      }
      return b;
    });

    if (Object.keys(extractedServices).length > 0) {
      setServicesById(prev => ({ ...prev, ...extractedServices }));
    }
    return normalizedBookings;
  }, []);

  const computeDatePickerRect = useCallback(() => {
    if (!pickerWrapRef.current || typeof window === 'undefined') return null;
    const rect = pickerWrapRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || 0;
    const viewportHeight = window.innerHeight || 0;
    const margin = 12;
    const estimatedWidth = 360;
    const estimatedHeight = 360;

    let left = rect.left;
    if (left + estimatedWidth > viewportWidth - margin) {
      left = Math.max(margin, viewportWidth - estimatedWidth - margin);
    }
    if (left < margin) left = margin;

    let top = rect.bottom + margin;
    if (top + estimatedHeight > viewportHeight - margin) {
      const above = rect.top - estimatedHeight - margin;
      top = above >= margin ? above : Math.max(margin, viewportHeight - estimatedHeight - margin);
    }

    return { top, left, width: estimatedWidth, height: estimatedHeight };
  }, []);
  // Date range filter helper
  const dateMatches = useCallback((b) => {
    const y = ymd(b?.date);
    if (!y) return !dateFrom && !dateTo; // if no date on booking, only include when no range set
    if (dateFrom && y < dateFrom) return false;
    if (dateTo && y > dateTo) return false;
    return true;
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const sel = dateRange?.[0];
    if (!sel?.startDate || !sel?.endDate) {
      if (dateFrom) setDateFrom('');
      if (dateTo) setDateTo('');
      return;
    }
    const from = ymd(sel.startDate);
    const to = ymd(sel.endDate);
    if (from && from !== dateFrom) setDateFrom(from);
    if (to && to !== dateTo) setDateTo(to);
  }, [dateRange, dateFrom, dateTo]);
  useEffect(() => {
    const onDocClick = (e) => {
      if (!pickerWrapRef.current) return;
      if (!pickerWrapRef.current.contains(e.target)) {
        setShowDateRangePicker(false);
        setDatePickerRect(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  useEffect(() => {
    if (!showDateRangePicker) return;
    const updatePosition = () => {
      const rect = computeDatePickerRect();
      if (rect) setDatePickerRect(rect);
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showDateRangePicker, computeDatePickerRect]);
  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const list = await fetchServiceCategories({ propertyId });
        // Expecting an array of strings; fallback to []
        setCategories(Array.isArray(list) ? list.filter(Boolean) : []);
      } catch (e) {
        console.warn('fetchServiceCategories failed:', e);
        setCategories([]);
      }
    })();
  }, [propertyId]);
  // Persist selectedCategory to localStorage
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, selectedCategory); } catch {}
  }, [selectedCategory]);
  const [recentHighlights, setRecentHighlights] = useState({});
  const highlightTimers = React.useRef({});
  const HIGHLIGHT_DURATION = 15000;
  const normalizeCategory = React.useCallback((value) => {
    if (!value) return '';
    return String(value).trim();
  }, []);

  const getBookingCategory = React.useCallback((booking) => {
    if (!booking) return '';
    const svc = servicesById[booking.tourId] || booking.service || {};
    const fromService = normalizeCategory(svc.category);
    if (fromService) return fromService;
    const fromType = normalizeCategory(booking.tourType);
    if (fromType) return fromType;
    return '';
  }, [servicesById, normalizeCategory]);

  const incrementNewBookingCount = useTourNotificationStore((s) => s.incrementNewBookingCount);
  useEffect(() => {
    // When bookings change, fetch missing service details
    const load = async () => {
      try {
        const ids = Array.from(new Set((bookings || []).map(b => b.tourId).filter(Boolean)));
        if (!ids.length) return;
        // Skip ids we already have
        const toFetch = ids.filter(id => !servicesById[id]);
        if (!toFetch.length) return;
        const map = await getToursByIds(toFetch);
        if (map && Object.keys(map).length) {
          setServicesById(prev => ({ ...prev, ...map }));
        }
      } catch (e) {
        console.warn('getToursByIds failed:', e);
      }
    };
    load();
  }, [bookings, servicesById]);

  const fetchBookings = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listTourBookings({ propertyId });
      const normalized = handleNewBookingsData(data);
      setBookings(Array.isArray(normalized) ? normalized : []);
      setRecentHighlights({});
    } catch (e) {
      console.warn('listTourBookings failed:', e);
      // do not surface inline UI error — keep it silent in UI
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [propertyId, handleNewBookingsData]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    if (!propertyId) return;

    socket.connect();

    const joinRoom = () => {
      try {
        socket.emit('joinTourBookingsRoom', propertyId);
      } catch (err) {
        console.warn('[socket] joinTourBookingsRoom emit failed', err);
      }
    };

    if (socket.connected) {
      joinRoom();
    }

    socket.on('connect', joinRoom);

    const sortBookings = (list) => {
      return [...list].sort((a, b) => {
        const toMs = (val) => {
          if (!val) return 0;
          const t = new Date(val).getTime();
          return Number.isFinite(t) ? t : 0;
        };
        return toMs(b.createdAt || b.date) - toMs(a.createdAt || a.date);
      });
    };

    const registerHighlight = (id) => {
      if (!id) return;
      setRecentHighlights((prev) => ({ ...prev, [id]: true }));
      if (highlightTimers.current[id]) {
        clearTimeout(highlightTimers.current[id]);
      }
      highlightTimers.current[id] = setTimeout(() => {
        setRecentHighlights((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        delete highlightTimers.current[id];
      }, HIGHLIGHT_DURATION);
    };

    const mergeBooking = (incoming) => {
      if (!incoming?._id) return;

      const [normalizedIncoming] = handleNewBookingsData([incoming]);
      if (!normalizedIncoming) return;

      let existed = false;
      setBookings((prev) => {
        existed = prev.some((b) => b._id === normalizedIncoming._id);
        const next = existed
          ? prev.map((b) => (b._id === normalizedIncoming._id ? { ...b, ...normalizedIncoming } : b))
          : [normalizedIncoming, ...prev];
        return sortBookings(next);
      });
      if (!existed) {
        registerHighlight(String(normalizedIncoming._id));
        if (propertyId) incrementNewBookingCount(propertyId);
      }
    };

    const handleNewBooking = (payload) => {
      const booking = payload?.booking || payload;
      mergeBooking(booking);
    };

    const handleUpdatedBooking = (payload) => {
      const booking = payload?.booking || payload;
      mergeBooking(booking);
    };

    const handleStatusUpdate = (payload) => {
      if (!payload) return;
      const booking = payload.booking || {};
      const merged = {
        ...booking,
        _id: booking?._id || payload.bookingId,
        status: booking?.status || payload.status,
        statusHistory: booking?.statusHistory || payload.statusHistory,
      };
      mergeBooking(merged);
    };

    socket.on('tourBooking:new', handleNewBooking);
    socket.on('tourBooking:updated', handleUpdatedBooking);
    socket.on('tourBookingStatusUpdate', handleStatusUpdate);

    return () => {
      socket.off('connect', joinRoom);
      socket.off('tourBooking:new', handleNewBooking);
      socket.off('tourBooking:updated', handleUpdatedBooking);
      socket.off('tourBookingStatusUpdate', handleStatusUpdate);
      socket.disconnect();
    };
  }, [propertyId, incrementNewBookingCount, handleNewBookingsData]);

  useEffect(() => () => {
    Object.values(highlightTimers.current).forEach((timer) => clearTimeout(timer));
    highlightTimers.current = {};
  }, []);

  const tourCategories = useMemo(() => {
    const base = Array.isArray(categories) ? categories : [];
    const derived = bookings.map(getBookingCategory).filter(Boolean);
    const unique = Array.from(new Set([...base, ...derived])).filter(Boolean).sort((a, b) => String(a).localeCompare(String(b)));
    const entries = unique.map((c) => [c, c]);
    return new Map([['all', 'All Tours'], ...entries]);
  }, [bookings, categories, getBookingCategory]);

  // Bookings filtered by selected category (used for status counts)
  const categoryFiltered = useMemo(() => {
    if (selectedCategory === 'all') return bookings;
    const selectedLower = selectedCategory.toLowerCase();
    return bookings.filter((b) => getBookingCategory(b).toLowerCase() === selectedLower);
  }, [bookings, selectedCategory, getBookingCategory]);

  useEffect(() => {
    if (selectedCategory !== 'all' && !tourCategories.has(selectedCategory)) {
      setSelectedCategory('all');
    }
  }, [selectedCategory, tourCategories]);

  const counts = useMemo(() => {
    const base = categoryFiltered.filter(dateMatches);
    const c = { all: base.length, waiting: 0, confirmed: 0, completed: 0, cancelled: 0 };
    for (const b of base) {
      const s = statusCategory(b?.status);
      if (s === 'waiting') c.waiting++;
      else if (s === 'confirmed') c.confirmed++;
      else if (s === 'completed') c.completed++;
      else if (s === 'cancelled') c.cancelled++;
    }
    return c;
  }, [categoryFiltered, dateMatches]);

  // New-booking badges per category (uses socket-driven recentHighlights)
  const recentCountsByCategory = useMemo(() => {
    const map = { all: 0 };
    for (const b of bookings) {
      if (!recentHighlights[b._id]) continue;
      map.all += 1;
      const cat = getBookingCategory(b);
      if (!cat) continue;
      map[cat] = (map[cat] || 0) + 1;
    }
    return map; // { all: n, [category]: n }
  }, [bookings, recentHighlights, getBookingCategory]);

  const filtered = useMemo(() => {
    let list = bookings;

    if (selectedCategory !== 'all') {
      const selectedLower = selectedCategory.toLowerCase();
      list = list.filter((b) => getBookingCategory(b).toLowerCase() === selectedLower);
    }
    // Date range filter (UTC YYYY-MM-DD)
    if (dateFrom || dateTo) {
      list = list.filter(dateMatches);
    }

    if (filter !== 'all') {
      list = list.filter((b) => {
        const s = statusCategory(b?.status);
        if (filter === 'waiting') return s === 'waiting';
        return s === filter; // confirmed | completed | cancelled
      });
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((b) =>
        (b.fullName || '').toLowerCase().includes(q) ||
        (b.email || '').toLowerCase().includes(q) ||
        (b.phone || '').toLowerCase().includes(q) ||
        (b.tourId || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [bookings, filter, query, selectedCategory, dateFrom, dateTo, dateMatches, getBookingCategory]);

  // --- Mutations (will gracefully handle 404 if endpoint not yet present) ---
  const setStatus = async (id, status) => {
    try {
      await setTourBookingStatus(id, status);
      await fetchBookings();
    } catch (e) {
      console.warn('setStatus failed:', e?.message);
      alert(`Failed to set status: ${e?.message || 'Unknown error'}`);
    }
  };

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const reasonInputRef = useRef(null);

  const CANCELLATION_REASONS = ['Guest request', 'Driver not available', 'Tour unavailable', 'Other'];

  const handleRequestCancel = useCallback((booking) => {
    setCancelTarget(booking);
    setCancellationReason('');
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    const booking = cancelTarget;
    try {
      await cancelTourBooking(booking._id, cancellationReason);
      await fetchBookings();
    } catch (e) {
      console.warn('cancel failed:', e?.message);
      alert(`Failed to cancel: ${e?.message || 'Unknown error'}`);
    } finally {
      setCancelTarget(null);
      setCancellationReason('');
    }
  }, [cancelTarget, cancellationReason, fetchBookings]);

  const confirmBooking = (b) => setStatus(b._id, 'Confirmed');
  const completeBooking = (b) => setStatus(b._id, 'Completed');

  // --- UI helpers ---
  const StatusPill = ({ status }) => {
    // Minimalist filled pill, calm colors
    const map = {
      waiting: { bg: '#FFF7ED', fg: '#B45309', label: 'Waiting', fontWeight: 600 },
      confirmed: { bg: '#DCFCE7', fg: '#15803D', label: 'Confirmed', fontWeight: 600 },
      completed: { bg: '#DBEAFE', fg: '#2563EB', label: 'Completed', fontWeight: 600 },
      cancelled: { bg: '#FEE2E2', fg: '#B91C1C', label: 'Cancelled', fontWeight: 600 },
      canceled: { bg: '#FEE2E2', fg: '#B91C1C', label: 'Cancelled', fontWeight: 600 },
    };
    const key = statusCategory(status);
    const s = map[key] || map.confirmed;
    return (
      <span style={{
        background: s.bg,
        color: s.fg,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: s.fontWeight,
        padding: '3px 14px',
        border: 'none',
        letterSpacing: 0.2,
        display: 'inline-block',
        minWidth: 70,
        textAlign: 'center',
      }}>{s.label}</span>
    );
  };

  // --- Card UI helpers (restaurant-style) ---
  // Compact stepper: inline dots + labels in a single slim row
  const CompactStepper = ({ status }) => {
    const value = statusCategory(status);
    const stage = value === 'waiting' ? 1 : value === 'confirmed' ? 2 : value === 'completed' ? 3 : 1;
    const Dot = ({ active }) => (
      <div style={{
        width: 12, height: 12, borderRadius: 999,
        background: active ? '#C8A76A' : '#E5E7EB',
        border: `1px solid ${active ? '#C8A76A' : '#E5E7EB'}`
      }} />
    );
    const Label = ({ text, active }) => (
      <span style={{ fontSize: 12, color: active ? '#C8A76A' : '#6B7280', fontWeight: active ? 600 : 400 }}>{text}</span>
    );
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <Dot active={stage >= 1} />
        <Label text="Waiting" active={stage === 1} />
        <div style={{ flex: 1, height: 2, background: stage >= 2 ? '#C8A76A' : '#E5E7EB' }} />
        <Dot active={stage >= 2} />
        <Label text="Confirmed" active={stage === 2} />
        <div style={{ flex: 1, height: 2, background: stage >= 3 ? '#C8A76A' : '#E5E7EB' }} />
        <Dot active={stage >= 3} />
        <Label text="Completed" active={stage === 3} />
      </div>
    );
  };

  const StatusLabel = ({ status }) => {
    const map = {
      1: 'Awaiting confirmation',
      2: 'Confirmed',
      3: 'In preparation',
      4: 'Ready',
      5: 'Completed',
    };
    const key = statusCategory(status);
    const label = key === 'waiting' ? map[1] : key === 'confirmed' ? map[2] : key === 'completed' ? map[5] : key === 'cancelled' ? 'Cancelled' : map[2];
    return <div style={{ color: '#6B7280', fontWeight: 600 }}>{label}</div>;
  };

  const Card = ({ b, isRecent }) => {
    const createdStr = fmtDateTime(b.createdAt || b.date);
    const tourDateStr = fmtDate(b.date);
    const addOns = [b.addSunrise ? 'Sunrise' : null, b.addBackTown ? 'Back to town' : null].filter(Boolean).join(' + ');

    const [open, setOpen] = useState(false);
    const [itemsOpen, setItemsOpen] = useState(false);

    return (
      <div
        style={{
          background: isRecent ? '#FFF7ED' : '#fff',
          border: isRecent ? '1px solid #FBBF24' : '1px solid #E5E7EB',
          borderRadius: 10,
          boxShadow: isRecent ? '0 8px 18px rgba(251, 191, 36, 0.18)' : 'none',
          padding: 12,
          marginBottom: 10,
          transition: 'box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease',
          cursor: 'default',
        }}
        onMouseEnter={(e) => {
          if (isRecent) {
            e.currentTarget.style.boxShadow = '0 10px 24px rgba(251, 191, 36, 0.22)';
          } else {
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(17, 24, 39, 0.08)'; // soft elevation
          }
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = isRecent
            ? '0 8px 18px rgba(251, 191, 36, 0.18)'
            : 'none';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#111827', letterSpacing: 0.1 }}>{`#${shortId(b._id)}`}</div>
            {!!b.roomNumber && <div style={{ color: '#374151', fontWeight: 500, fontSize: 13, marginTop: 1 }}>Room {b.roomNumber}</div>}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 10,
                fontSize: 13,
                marginTop: 6,
                color: '#6B7280',
              }}
            >
              <span>
                <strong style={{ color: '#111827', fontWeight: 600 }}>
                  {b.fullName || b.email || 'Guest'}
                </strong>
              </span>
              <span>•</span>
              <span>
                Total: <strong style={{ color: '#111827' }}>{money(b.total)}</strong>
              </span>
              <span>•</span>
              <span>
                <span style={{ color: '#6B7280' }}>Tour Date:</span>{' '}
                <strong style={{ color: '#111827' }}>
                  {`${fmtDate(b.date)}${b.time ? ' · ' + fmtTime(b.time) : ''}`}
                </strong>
              </span>
              <span>•</span>
              <span>
                <span style={{ color: '#6B7280' }}>Created:</span>{' '}
                <strong style={{ color: '#111827' }}>{fmtDateTime(b.createdAt)}</strong>
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StatusPill status={b.status} />
            {isRecent && (
              <span
                style={{
                  background: '#F97316',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '2px 10px',
                  borderRadius: 999,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                New
              </span>
            )}
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Hide details' : 'Show details'}
              title={open ? 'Hide details' : 'Show details'}
              style={{
                border: 'none',
                background: '#F9FAFB',
                padding: '5px 8px',
                borderRadius: 8,
                cursor: 'pointer',
                lineHeight: 1,
                fontSize: 12,
                color: '#6B7280',
                transition: 'background 0.13s',
              }}
            >
              {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {/* Compact Stepper */}
        <div style={{ marginTop: 8, marginBottom: 8 }}>
          <CompactStepper status={b.status} />
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: '#E5E7EB', margin: '8px 0' }} />

        {/* Body */}
        <div style={{}}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontWeight: 500, color: '#6B7280', fontSize: 13 }}>Items (1)</div>
            <button
              onClick={() => setItemsOpen((v) => !v)}
              aria-label={itemsOpen ? 'Hide items' : 'Show items'}
              title={itemsOpen ? 'Hide items' : 'Show items'}
              style={{
                border: 'none',
                background: '#F9FAFB',
                padding: '5px 8px',
                borderRadius: 8,
                cursor: 'pointer',
                lineHeight: 1,
                fontSize: 12,
                color: '#6B7280',
                transition: 'background 0.13s',
              }}
            >
              {itemsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
          {itemsOpen ? (
            <div style={{ color: '#6B7280', fontSize: 14 }}>
              <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center' }}>
                {(() => {
                  const svc = servicesById[b.tourId] || b.service || {};
                  const title = svc?.title || `Tour #${String(b.tourId || '').slice(0, 6)}`;
                  const thumb = Array.isArray(svc?.imageUrls) ? svc.imageUrls[0] : undefined;
                  return (
                    <>
                      {thumb ? (
                        <img
                          src={thumb}
                          alt="tour"
                          style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', marginRight: 10, border: '1px solid #E5E7EB', background: '#FAFAFA' }}
                        />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: 6, marginRight: 10, background: '#F3F4F6', border: '1px solid #E5E7EB' }} />
                      )}
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ color: '#111827', fontWeight: 600 }}>{title}</span>
                        {b.time && <span style={{ color: '#6B7280', fontSize: 13 }}>{`• ${fmtTime(b.time)}`}</span>}
                      </div>
                    </>
                  );
                })()}
              </div>
              {!!b.transportType && <div style={{ color: '#374151', fontSize: 13, marginTop: 2 }}>Transport: <span style={{ fontWeight: 500 }}>{b.transportType} {b.transportPax && `(${b.transportPax})`}</span></div>}
              {!!addOns && <div style={{ color: '#374151', fontSize: 13, marginTop: 1 }}>Add-ons: <span style={{ fontWeight: 500 }}>{addOns}</span></div>}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', color: '#374151', fontSize: 13 }}>
              {(() => {
                const svc = servicesById[b.tourId] || b.service || {};
                const title = svc?.title || `Tour #${String(b.tourId || '').slice(0, 6)}`;
                return (
                  <>
                    <span style={{ color: '#111827', fontWeight: 600 }}>{title}</span>
                    {b.time && <span style={{ color: '#6B7280', marginLeft: 8 }}>{`• ${fmtTime(b.time)}`}</span>}
                    {!!b.transportType && <span style={{ color: '#6B7280', marginLeft: 8 }}>{`• ${b.transportType} ${b.transportPax ? `(${b.transportPax})` : ''}`}</span>}
                    {!!addOns && <span style={{ color: '#6B7280', marginLeft: 8 }}>{`• ${addOns}`}</span>}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {!!b.specialRequest && (
          <div style={{
            marginTop: 14,
            background: '#FAFAFA',
            border: 'none',
            padding: 13,
            borderRadius: 10,
            fontSize: 14,
          }}>
            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 500, marginBottom: 4, letterSpacing: 0.1 }}>Note</div>
            <div style={{ fontSize: 14, color: '#111827', fontWeight: 400 }}>{b.specialRequest}</div>
          </div>
        )}

        {statusCategory(b.status) === 'cancelled' && b.cancellationReason && (
          <div style={{
            marginTop: 14,
            background: '#FEF2F2', // red-50
            border: '1px solid #FCA5A5', // red-300
            padding: 13,
            borderRadius: 10,
            fontSize: 14,
          }}>
            <div style={{ fontSize: 12, color: '#991B1B', fontWeight: 500, marginBottom: 4, letterSpacing: 0.1 }}>Cancellation Reason</div>
            <div style={{ fontSize: 14, color: '#B91C1C', fontWeight: 400 }}>{b.cancellationReason}</div>
          </div>
        )}

        {/* Expandable details */}
        {open && (
          <div style={{
            marginTop: 10,
            background: '#FAFAFA',
            border: 'none',
            padding: 12,
            borderRadius: 10,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Booking Reference</div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{shortId(b._id)}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Tour ID</div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{b.tourId || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Guest email</div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{b.email || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Guest phone</div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{b.phone || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Departure</div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{b.time ? fmtTime(b.time) : '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Transport</div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{b.transportType} {b.transportPax && `(${b.transportPax})`}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Add-ons</div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{addOns || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Room</div>
                <div style={{ fontWeight: 600, color: '#111827' }}>{b.roomNumber || '-'}</div>
              </div>
            </div>
            <div style={{ height: 1, background: '#E5E7EB', margin: '14px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>Subtotal</div>
                <div style={{ fontWeight: 700 }}>{money(b.subtotal)}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>VAT</div>
                <div style={{ fontWeight: 700 }}>{money(b.vat)}</div>
              </div>
              <div style={{ gridColumn: '1 / span 2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Total</div>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{money(b.total)}</div>
              </div>
            </div>
            {(b.included || b.notIncluded) && (
              <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {!!b.included && (
                  <div>
                    <div style={{ color: '#6B7280', fontSize: 12, fontWeight: 400 }}>Included</div>
                    <div style={{ fontSize: 13, color: '#15803D' }}>{b.included}</div>
                  </div>
                )}
                {!!b.notIncluded && (
                  <div>
                    <div style={{ color: '#6B7280', fontSize: 12, fontWeight: 400 }}>Not included</div>
                    <div style={{ fontSize: 13, color: '#B45309' }}>{b.notIncluded}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
          {statusCategory(b.status) === 'waiting' && (
            <>
              <button
                onClick={() => confirmBooking(b)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                  background: '#fff',
                  color: '#111827',
                  fontWeight: 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'background 0.13s',
                }}
              >
                Confirm
              </button>
              <button
                onClick={() => handleRequestCancel(b)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  border: '1px solid #FCA5A5',
                  background: '#FEF2F2',
                  color: '#B91C1C',
                  fontWeight: 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'background 0.13s',
                }}
              >
                Cancel
              </button>
            </>
          )}
          {statusCategory(b.status) === 'confirmed' && (
            <>
              <button
                onClick={() => completeBooking(b)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                  background: '#fff',
                  color: '#111827',
                  fontWeight: 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'background 0.13s',
                }}
              >Complete</button>
              <button
                onClick={() => handleRequestCancel(b)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  border: '1px solid #FCA5A5',
                  background: '#FEF2F2',
                  color: '#B91C1C',
                  fontWeight: 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  transition: 'background 0.13s',
                }}
              >Cancel</button>
            </>
          )}
        </div>
      </div>
    );
  };

  // Dynamic heading based on selected category
  const selectedTitle = tourCategories.get(selectedCategory) || 'All Tours';
  const headingLabel = selectedCategory === 'all' ? 'Tour bookings' : `${selectedTitle} bookings`;

  return (
    <div style={{ display: 'flex', padding: 16 }}>
      <div style={{ width: 240, paddingRight: 16, borderRight: '1px solid #E5E7EB' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, paddingLeft: 12 }}>Service Categories</h3>
        {Array.from(tourCategories.entries()).map(([id, title]) => (
  <div key={id} style={{ position: 'relative', marginBottom: 6 }}>
    <button
      onClick={() => setSelectedCategory(id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '10px 12px 10px 14px',
        borderRadius: 8,
        border: selectedCategory === id ? '1px solid #E5E7EB' : '1px solid transparent',
        textAlign: 'left',
        background: selectedCategory === id ? '#F8FAFC' : 'transparent',
        boxShadow: selectedCategory === id ? '0 1px 0 rgba(0,0,0,0.02)' : 'none',
        fontWeight: selectedCategory === id ? 700 : 500,
        color: selectedCategory === id ? '#111827' : '#374151',
        cursor: 'pointer',
        fontSize: 14,
        // Animation: scale transform, background, border-color
        transform: 'scale(1)',
        transition: 'transform 0.12s ease-out, background 0.12s, border-color 0.12s',
      }}
      onMouseEnter={(e) => {
        if (selectedCategory !== id) e.currentTarget.style.background = '#F9FAFB';
      }}
      onMouseLeave={(e) => {
        if (selectedCategory !== id) e.currentTarget.style.background = 'transparent';
        // Reset transform on mouse leave (for animation)
        e.currentTarget.style.transform = 'scale(1)';
      }}
      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.96)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: selectedCategory === id ? '#C8A76A' : '#D1D5DB',
        }}
      />
      <span>{title}</span>
      {(() => {
        const count = id === 'all' ? (recentCountsByCategory.all || 0) : (recentCountsByCategory[id] || 0);
        if (!count) return null;
        return (
          <span
            style={{
              marginLeft: 'auto',
              background: '#f91616ff',
              color: '#fff',
              borderRadius: 999,
              fontSize: 11,
              padding: '2px 6px',
              lineHeight: 1,
              minWidth: 16,
              textAlign: 'center',
              boxShadow: '0 1px 0 rgba(0,0,0,0.06)'
            }}
          >
            {count}
          </span>
        );
      })()}
    </button>
  </div>
))}
      </div>
      <div style={{ flex: 1, paddingLeft: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <strong style={{ fontSize: 18 }}>{headingLabel}</strong>
          <div style={{ flex: 1 }} />
          <input
            placeholder="Search by name, email, phone, tourId"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E7EB', minWidth: 260 }}
          />
          <div ref={pickerWrapRef} style={{ position: 'relative', zIndex: 1000, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDateRangePicker((v) => {
                  const next = !v;
                  if (next) {
                    const rect = computeDatePickerRect();
                    setDatePickerRect(rect);
                  } else {
                    setDatePickerRect(null);
                  }
                  return next;
                });
              }}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#111827' }}
              title="Pick date range"
            >
              {dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : 'Filter by date range'}
            </button>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); setDateRange([]); }}
                style={{ padding: '8px 10px', borderRadius: 8, background: '#F3F4F6', border: '1px solid #E5E7EB', color: '#374151' }}
                title="Clear date filter"
              >
                Clear
              </button>
            )}
            {showDateRangePicker && (
              <DateRangePicker
                dateRange={dateRange.length ? dateRange : createDefaultRange()}
                setDateRange={setDateRange}
                style={datePickerRect ? undefined : { position: 'relative', top: 'auto', left: 'auto', boxShadow: 'none', border: 'none' }}
                anchorRect={datePickerRect}
              />
            )}
          </div>
          <button onClick={fetchBookings} disabled={loading} style={{ padding: '8px 12px', borderRadius: 8, marginLeft: 8 }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[
            { key: 'all', label: `All (${counts.all})` },
            { key: 'waiting', label: `Waiting (${counts.waiting})` },
            { key: 'confirmed', label: `Confirmed (${counts.confirmed})` },
            { key: 'completed', label: `Completed (${counts.completed})` },
            { key: 'cancelled', label: `Cancelled (${counts.cancelled})` },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid #E5E7EB',
                background: filter === key ? '#111827' : '#FFF',
                color: filter === key ? '#FFF' : '#111827',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {label}
            </button>
          ))}
        </div>


        {/* Cards */}
        {filtered.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#6B7280', border: '1px dashed #E5E7EB', borderRadius: 12 }}>
            {loading ? 'Loading bookings…' : 'No bookings found'}
          </div>
        ) : (
          <div style={{ marginTop: 4 }}>
        {filtered.map((b) => (
          <Card key={b._id} b={b} isRecent={!!recentHighlights[b._id]} />
        ))}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancel this booking?"
        description={
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This will mark the tour booking as cancelled. Guests will no longer see it as active.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {CANCELLATION_REASONS.map((reason) => (
                <Chip
                  key={reason}
                  label={reason}
                  onClick={() => {
                    if (reason === 'Other') {
                      setCancellationReason('');
                      setTimeout(() => reasonInputRef.current?.focus(), 100);
                    } else {
                      setCancellationReason(reason);
                    }
                  }}
                  color={CANCELLATION_REASONS.includes(cancellationReason) && cancellationReason === reason ? 'primary' : 'default'}
                  variant={CANCELLATION_REASONS.includes(cancellationReason) && cancellationReason === reason ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
            <TextField
              inputRef={reasonInputRef}
              autoFocus
              margin="dense"
              id="cancellation-reason"
              label="Reason for cancellation"
              type="text"
              fullWidth
              variant="outlined"
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        }
        confirmLabel="Yes, cancel it"
        cancelLabel="No, keep it"
        onClose={() => {
          setCancelTarget(null);
          setCancellationReason('');
        }}
        onCancel={() => {
          setCancelTarget(null);
          setCancellationReason('');
        }}
        onConfirm={handleConfirmCancel}
        confirmDisabled={!cancellationReason.trim()}
      />
    </div>
  );
}
